import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SpeedTrace from '../Analysis/SpeedTrace';
import { getPublicTrackLap3D } from '../../services/api';
import styles from './PublicTrack.module.css';

// Same lazy boundary the app uses — three.js is a large chunk and a visitor who
// never scrolls to the 3D section should never download it.
const Lap3DView = lazy(() => import('../Lap3D/Lap3DView'));

const LAP_COLOR = '#4fa3ff';

/**
 * The interactive half of a public track page: one recorded lap, in 3D, over a
 * speed trace that shares its cursor.
 *
 * The 3D document is ~200 KB and the scenery bake is a good deal more, so none
 * of it is fetched until the section is scrolled into view. Everything above it
 * on the page — the map, the numbers, the layout — is already rendered by then.
 *
 * There is no session id anywhere in here. The document arrives from
 * /api/public/tracks/<slug>/lap3d already stripped of driver, car and session,
 * and is handed to Lap3DView as `compareDoc` so the view has no reason (and no
 * means) to ask an authenticated endpoint for one.
 */
export default function TrackLapIsland({ slug, trace, displayName }) {
  const hostRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [doc, setDoc] = useState(null);
  const [state, setState] = useState('idle');   // idle | loading | ready | unavailable

  useEffect(() => {
    const el = hostRef.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setVisible(true);
        observer.disconnect();
      }
    }, { rootMargin: '300px' });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // A ref, not the `state` value, decides whether the fetch has already been
  // started — and the result is never discarded on cleanup. Guarding on state
  // instead deadlocks under StrictMode's double-mount: the first run moves state
  // to 'loading' and is then torn down, and the second run bails on that very
  // guard, so the only in-flight request lands with its results thrown away and
  // the section says "Loading…" forever.
  const startedFor = useRef(null);

  useEffect(() => {
    if (!visible || startedFor.current === slug) return;
    startedFor.current = slug;
    setState('loading');
    getPublicTrackLap3D(slug)
      .then((payload) => {
        // null is the honest answer when the venue's scenery has not been baked
        // yet: the server refuses to bake one on an anonymous request.
        if (!payload?.compare) { setState('unavailable'); return; }
        setDoc(payload);
        setState('ready');
      })
      .catch(() => setState('unavailable'));
  }, [visible, slug]);

  // One lap, one color, no identity. Lap3DView reads `color` off these and
  // nothing else, because `compareDoc` short-circuits the fetch that would
  // otherwise need identifiers.
  const laps = useMemo(() => [{ color: LAP_COLOR }], []);
  const lapTraces = useMemo(() => ({ 0: trace }), [trace]);
  const selectedLaps = useMemo(() => [0], []);
  // Cursor sync between the trace and the 3D view runs on arc length, so the
  // view only needs the one lap's points however it asks for them.
  const getLapPoints = useCallback(() => trace, [trace]);

  return (
    <div className={styles.lapIsland} ref={hostRef}>
      <div className={styles.stage3d}>
        {state === 'ready' && doc ? (
          <Suspense fallback={<div className={styles.stageNote}>Loading the 3D view…</div>}>
            <Lap3DView
              laps={laps}
              compareDoc={doc.compare}
              groundDoc={doc.ground}
              getLapPoints={getLapPoints}
              autoPlayWhenReady={false}
              publicMode
            />
          </Suspense>
        ) : (
          <div className={styles.stageNote}>
            {state === 'unavailable'
              ? `The 3D view of ${displayName} isn’t ready yet — the map and lap data above are all live.`
              : 'Loading the 3D view…'}
          </div>
        )}
      </div>

      <div className={styles.traceWrap}>
        <SpeedTrace
          lapTraces={lapTraces}
          selectedLaps={selectedLaps}
          customColors1={{ 0: LAP_COLOR }}
          height={220}
          trackName={displayName}
          driverName1="Reference Lap"
        />
      </div>
    </div>
  );
}
